<template>
  <div class="flex flex-col h-full">
    <ThreadsNav :show-back="true" :show-center="false">
      <template #left>
        <div
          v-if="!loading"
          class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
        >
          <div
            :class="[
              'rounded-full w-2 h-2',
              isSavingStatus ? 'bg-warning' : 'bg-success',
            ]"
          />
          <span>
            {{ t(`User.settings${isSavingStatus ? "Unsaved" : "Saved"}`) }}
          </span>
        </div>
      </template>
    </ThreadsNav>

    <div class="flex-1 overflow-y-auto" v-if="relationship">
      <!-- First character's relationship view -->
      <div class="mb-2 px-4">
        <span class="text-primary" @click="clickMate(relationship?.sourceMate)">
          {{ relationship?.sourceMate?.name }}
        </span>
        眼中，
        <span class="text-primary" @click="clickMate(relationship?.targetMate)">
          {{ relationship?.targetMate?.name }}
        </span>
        是：
      </div>

      <VanCellGroup inset>
        <VanField
          v-model="relationship.name"
          label="关系"
          placeholder="关系"
          @input="onSave(relationship)"
          label-align="top"
        />
        <VanField
          v-model="relationship.description"
          type="textarea"
          label="关系介绍"
          placeholder="关系介绍"
          label-align="top"
          @input="onSave(relationship)"
        />
      </VanCellGroup>

      <!-- Second character's relationship view -->
      <div class="my-2 px-4">
        <span
          class="text-primary"
          @click="clickMate(relationship?.reverse?.sourceMate)"
        >
          {{ relationship.reverse?.sourceMate?.name }}
        </span>
        眼中，
        <span
          class="text-primary"
          @click="clickMate(relationship?.reverse?.targetMate)"
        >
          {{ relationship.reverse?.targetMate?.name }}
        </span>
        是：
      </div>

      <VanCellGroup inset>
        <VanField
          v-model="relationship.reverse!.name"
          label="关系"
          placeholder="关系"
          label-align="top"
          @input="onSave(relationship.reverse!)"
        />
        <VanField
          v-model="relationship.reverse!.description"
          type="textarea"
          label="关系介绍"
          placeholder="关系介绍"
          label-align="top"
          @input="onSave(relationship.reverse!)"
        />
      </VanCellGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useInteractive } from "@/stores/interactive";
import { MateRelationship, Mate } from "@/types/entity";
import { useI18n } from "vue-i18n";
import { debounce } from "lodash";

const route = useRoute();
const router = useRouter();
const interactiveStore = useInteractive();
const { t } = useI18n();

const loading = ref(false);
const isSavingStatus = ref(false);
const relationship = ref<MateRelationship | null>(null);

let isSaving = false;
// 创建防抖版本的保存函数
const debouncedSave = debounce(async (relationship: MateRelationship) => {
  if (isSaving) return; // 如果已经在保存，则不重复执行

  try {
    isSaving = true;
    await interactiveStore.updateRelationship(relationship.id, {
      name: relationship.name,
      description: relationship.description,
    });
  } catch (error) {
    console.error("保存关系失败:", error);
  } finally {
    isSavingStatus.value = false;
    isSaving = false;
  }
}, 500); // 500毫秒的防抖延迟

const onSave = (relationship: MateRelationship) => {
  isSavingStatus.value = true;
  debouncedSave(relationship);
};

const clickMate = (mate?: Mate) => {
  if (!mate) return;
  router.push({
    name: "mateHome",
    params: { uuid: mate.uuid },
  });
};

onMounted(async () => {
  const relationshipId = parseInt(route.query.id as string);
  if (!relationshipId) return;

  try {
    const r = interactiveStore.myRelationshipList.find(
      (v) => v.id === relationshipId
    );
    if (!r) return router.back();
    relationship.value = r;
  } catch (error) {
    console.error("Failed to fetch relationship details:", error);
  }
});
</script>
